import { all, put, select, takeLatest } from 'redux-saga/effects';
import { LaboratoryActionTypes } from './lab-actions';
import { FinancialActionTypes } from '@store/financials/financials-actions';
import { AlertActionType } from '@store/alert/alert-action';
import labsStoreDataApi from '@api/labs/labs-store';
import labsTestItemsApi from '@api/labs/labs-test-items';
import prescriptionStoreDataApi from '@api/prescription/prescription-store';
import uploadPrescriptionFileApi from '@api/prescription/upload-prescription';
import { labStore } from './lab-store';
import ReactGA from 'react-ga4';
import { setNewAddressWatcher } from '@store/client/contact-information/contact-information-middleware';
import { homeStore } from '@store/home-screen/home-store';
import routes from '@routes';
import getParseRoute from '@utils/helpers/parse-route';
import getDictionary from '@dictionaries-client';

function* getTestWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { laboratory: { messages } } } = getDictionary(locale);
  try {
    const { hashedCityId, hashedServiceTypeId, hashedLabId } = yield select(labStore);

    const Lab: Array<object> = yield labsTestItemsApi({
      hashedLabId: hashedLabId,
      hashedCityId: hashedCityId,
      hashedServiceTypeId: hashedServiceTypeId,
      cache: 'no-cache',
    });

    if (!(Lab && Lab.length > 0)) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.get_info_city_error,
        severity: 'warning',
      });
    }
    yield put({
      type: LaboratoryActionTypes.SET_SERVICE_TYPES,
      data: {
        laboratoryData: Lab,
      },
    });
  } catch (error: any) {
    const { navigation } = yield select(homeStore);
    if (error?.code === 422) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.get_info_error,
        severity: 'danger',
      });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.get_info_city_error,
        severity: 'danger',
      });
    }
    yield navigation.back();
  }
}

function* storeLabDataWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { laboratory: { messages } } } = getDictionary(locale);
  try {
    const { contactInfo: { addressesList }, auth: { firstName, lastName, nationalId, sex }, clientSuggestedType, clientSuggestedTime, clientSuggestedRange, hashedServiceTypeId, orderCount, selectServiceId, recipients, hashedLabId } = yield select(labStore);
    const { navigation, locale } = yield select(homeStore);

    if (!addressesList.length) {
      yield setNewAddressWatcher();
    }
    ReactGA.ga('send', 'event', 'laboratory', 'create', 'laboratory create order');

    const { contactInfo: { selectedAddressId } } = yield select(labStore);

    const labResponse: { data: { route_key: string } } = yield labsStoreDataApi({
      labId: hashedLabId,
      serviceId: selectServiceId,
      serviceSubTypeId: hashedServiceTypeId,
      orderCount: orderCount.toString(),
      contributors: recipients,
      clientContactId: selectedAddressId,
      applicantName: firstName,
      applicantLastName: lastName,
      applicantCode: nationalId,
      gender: sex,
      clientSuggestedType: clientSuggestedType,
      clientSuggestedTime: clientSuggestedTime,
      clientSuggestedRange: clientSuggestedRange,
      cache: 'no-cache',
    });
    yield put({
      type: FinancialActionTypes.SET_ORDER_ID,
      data: {
        orderItemId: labResponse?.data?.route_key,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: messages.store_test_success,
      severity: 'success',
    });
    if (window.location.pathname.includes('/mobile')) {
      yield navigation.replace(getParseRoute({ pathname: `/mobile/financials/order/${labResponse?.data?.route_key}`, locale: locale }));
    } else {
      yield navigation.replace(getParseRoute({ pathname: `/financials/order/${labResponse?.data?.route_key}`, locale: locale }));
    }

    yield put({
      type: LaboratoryActionTypes.SET_STORE_LOADING,
    });
  } catch (error: any) {
    if (error?.code === 422) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.store_test_error_retry,
        severity: 'danger',
      });
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || messages.store_test_error,
        severity: 'danger',
      });
    }
    yield put({
      type: LaboratoryActionTypes.SET_STORE_LOADING,
    });
  }
}

function* storePrescriptionDataWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { laboratory: { messages } } } = getDictionary(locale);
  try {
    const { prescriptionFile, prescriptionText, recipients, auth: { firstName, lastName, nationalId, sex }, hashedLabId, contactInfo: { addressesList }, clientSuggestedType, clientSuggestedTime, clientSuggestedRange } = yield select(labStore);
    const { navigation, locale } = yield select(homeStore);

    if (!addressesList.length) {
      yield setNewAddressWatcher();
    }
    ReactGA.ga('send', 'event', 'laboratory', 'create', 'laboratory create order');

    const uploadFileResponse: { data: any } = yield uploadPrescriptionFileApi({ file: prescriptionFile, cache: 'no-cache' });

    const { contactInfo: { selectedAddressId } } = yield select(labStore);
    if (uploadFileResponse) {
      const PrescriptionResponse: { data: { order_reference_id_affix: string } } = yield prescriptionStoreDataApi({
        labId: hashedLabId,
        serviceSubTypeId: 'r1xjgX98742',
        orderCount: '1',
        contributors: recipients,
        clientContactId: selectedAddressId,
        applicantName: firstName,
        applicantLastName: lastName,
        applicantCode: nationalId,
        gender: sex,
        clientSuggestedType: clientSuggestedType,
        clientSuggestedTime: clientSuggestedTime,
        clientSuggestedRange: clientSuggestedRange,
        file: {
          '1': uploadFileResponse,
        },
        fileComment: prescriptionText,
        cache: 'no-cache',
      });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.store_prescription_success,
        severity: 'success',
      });
      yield put({
        type: LaboratoryActionTypes.SET_STORE_LOADING,
      });
      if (window.location.pathname.includes('/mobile')) {
        yield navigation.replace(`/mobile${getParseRoute({ pathname: routes['route.client.laboratory-order-details'], query: { id: PrescriptionResponse?.data?.order_reference_id_affix }, locale: locale })}`);
      } else {
        yield navigation.replace(getParseRoute({ pathname: routes['route.client.laboratory-order-details'], query: { id: PrescriptionResponse?.data?.order_reference_id_affix }, locale: locale }));
      }
    } else {
      yield put({
        type: LaboratoryActionTypes.SET_STORE_LOADING,
      });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.store_prescription_error,
        severity: 'danger',
      });
    }
  } catch (error: any) {
    yield put({
      type: LaboratoryActionTypes.SET_STORE_LOADING,
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.store_prescription_error,
      severity: 'danger',
    });
  }
}

function* labMiddleware() {
  yield all([
    takeLatest(LaboratoryActionTypes.GET_SERVICE_TYPES, getTestWatcher),
    takeLatest(LaboratoryActionTypes.STORE_LAB_DATA, storeLabDataWatcher),
    takeLatest(LaboratoryActionTypes.STORE_PRESCRIPTION_DATA, storePrescriptionDataWatcher),
  ]);
}

export default labMiddleware;
